import type { HistoryRow, Report } from '#shared/types';

/** One test the patient has had, anchored on its newest report. */
export interface TrendTest {
  test_id: number;
  name: string;
  /** The newest report of this test — the anchor the history query wants. */
  report_id: number;
  visits: number;
}

export interface ComparisonRow {
  parameter_id: number;
  name: string;
  unit: string;
  reference: string;
  retired: boolean;
  cells: HistoryRow['cells'];
}

type TrendReport = Pick<Report, 'id' | 'test_id' | 'name' | 'created_at'>;

/**
 * Every test in `reports`, with its visit count and its newest report, most
 * visited first and then by name.
 *
 * The anchor must be the *latest* visit, or the history query — which only
 * looks backwards — misses everything after it. On a tie the first report seen
 * stays the anchor.
 */
export function groupReportsByTest(reports: readonly TrendReport[]): TrendTest[] {
  const byTest = new Map<number, TrendTest>();
  // When each test's anchor was created. Kept alongside rather than looked up,
  // so finding the newest report is one pass over the list.
  const anchoredAt = new Map<number, string>();

  for (const report of reports) {
    const seen = byTest.get(report.test_id);
    if (!seen) {
      byTest.set(report.test_id, {
        test_id: report.test_id,
        name: report.name,
        report_id: report.id,
        visits: 1,
      });
      anchoredAt.set(report.test_id, report.created_at);
      continue;
    }
    seen.visits += 1;
    if (report.created_at > (anchoredAt.get(report.test_id) ?? '')) {
      seen.report_id = report.id;
      anchoredAt.set(report.test_id, report.created_at);
    }
  }

  return [...byTest.values()].sort((a, b) => b.visits - a.visits || a.name.localeCompare(b.name));
}

/** The test named in the URL, or the first one when it names none we know. */
export function pickActiveTest(
  tests: readonly TrendTest[],
  selectedTestId: string | null,
): TrendTest | undefined {
  return tests.find((test) => String(test.test_id) === selectedTestId) ?? tests[0];
}

/** History rows as table rows: the current visit first, then the earlier ones. */
export function buildComparisonRows(rows: readonly HistoryRow[]): ComparisonRow[] {
  return rows.map((row) => ({
    parameter_id: row.parameter_id,
    name: row.name,
    unit: row.unit,
    reference: row.normal_range,
    retired: row.retired,
    cells: [row.current, ...row.cells],
  }));
}
